import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Check } from "lucide-react";
import { listCohorts } from "@/lib/api";

// Shared cohort picker used on Applications, Dashboard and Finance. Starts
// with every cohort auto-selected (so stats/lists show something useful
// immediately) — the admin can then deselect down to a subset, or clear
// entirely to fall back to the old unfiltered "all" behavior. Hides itself
// entirely until at least one cohort has been seeded (direct Supabase
// editing, no admin UI for that yet) so pages don't show a useless empty
// dropdown.
//
// Picks PROGRAM-wise, not specialization-wise — cohorts differ by
// student_category_id (entity + program + specialization), so the same
// program (e.g. B.Tech) can have several cohort rows that only differ by
// specialization. Listing each one individually made the picker noisy and
// out of step with the Admin Overview's program-level grouping, so options
// here are grouped by entity+program+year; picking one toggles every
// specialization's cohort_id underneath it. Callers still just receive a
// flat cohort_id array — filtering elsewhere is unaffected.
export const CohortMultiSelect = ({ selected = [], onChange }) => {
  const [cohorts, setCohorts] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  // Guards the auto-select so it only ever fires once per mount — otherwise
  // a deliberate "clear all" by the admin would keep getting overridden.
  const autoSelectedRef = useRef(false);

  useEffect(() => {
    listCohorts().then((data) => {
      const list = Array.isArray(data) ? data : [];
      setCohorts(list);
      if (!autoSelectedRef.current && list.length > 0) {
        autoSelectedRef.current = true;
        onChange(list.map((c) => c.cohort_id));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const groups = useMemo(() => {
    const map = new Map();
    for (const c of cohorts) {
      const programLabel = (c.category_label || "Unknown").replace(/\s*\([^)]*\)/, "");
      const key = `${programLabel} · ${c.year}`;
      const g = map.get(key);
      if (g) {
        g.cohortIds.push(c.cohort_id);
        g.allInactive = g.allInactive && !c.is_active;
      } else {
        map.set(key, { key, label: key, cohortIds: [c.cohort_id], allInactive: !c.is_active });
      }
    }
    return Array.from(map.values());
  }, [cohorts]);

  if (!cohorts.length) return null;

  // Selection only ever happens in whole-group chunks (toggleGroup below), so
  // `selected` is always a union of complete groups — no partial-group state
  // to reconcile here.
  const toggleGroup = (group) => {
    const allSelected = group.cohortIds.every((id) => selected.includes(id));
    onChange(
      allSelected
        ? selected.filter((id) => !group.cohortIds.includes(id))
        : Array.from(new Set([...selected, ...group.cohortIds]))
    );
  };

  const selectedGroups = groups.filter((g) => g.cohortIds.every((id) => selected.includes(id)));
  const label =
    selected.length === 0 || selected.length === cohorts.length
      ? "All cohorts"
      : selectedGroups.length === 1
      ? selectedGroups[0].label
      : `${selectedGroups.length} program${selectedGroups.length === 1 ? "" : "s"}`;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="mu-btn-secondary text-sm"
        data-testid="btn-cohort-select"
      >
        {label} <ChevronDown className="h-3.5 w-3.5" />
      </button>
      {open && (
        <div
          className="absolute z-20 mt-2 w-72 rounded-xl border border-[#E5E7EB] bg-white shadow-lg p-2 max-h-72 overflow-y-auto"
          data-testid="cohort-select-panel"
        >
          {selected.length > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="w-full text-left text-xs text-[#B60F2A] font-medium px-2 py-1.5 hover:bg-[#FBE7EA]/40 rounded-lg"
              data-testid="btn-cohort-clear"
            >
              Clear selection (show all)
            </button>
          )}
          {groups.map((g) => {
            const active = g.cohortIds.every((id) => selected.includes(id));
            return (
              <label
                key={g.key}
                className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-[#F7F7F7] cursor-pointer text-sm"
                data-testid={`cohort-option-${g.key.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <input
                  type="checkbox"
                  checked={active}
                  onChange={() => toggleGroup(g)}
                  className="h-4 w-4 accent-[#B60F2A]"
                />
                <span className="flex-1 text-[#111]">{g.label}</span>
                {g.allInactive && <span className="text-[10px] text-[#9CA3AF]">inactive</span>}
                {active && <Check className="h-3.5 w-3.5 text-[#B60F2A]" />}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
};
